import { MagnifyingGlassSolid } from '@allocab/allocab-icons';
import { useMemo, useState } from 'react';

import { TextInput } from '../../components/atomic/TextInput';
import { GradientButton } from '../../components/button/GradientButton';
import { PrimaryButton } from '../../components/button/PrimaryButton';
import { SecondaryButton } from '../../components/button/SecondaryButton';
import { Modal } from '../../components/modal/Modal';
import { Spinner } from '../../components/Spinner';
import { useAssignDriverToVehicle } from '../../hooks/useAssignDriverToVehicle';
import useDebounce from '../../hooks/useDebounce';
import { useInfiniteFetchDrivers } from '../../hooks/useFetchDrivers';
import { ToastManager } from '../../services/toast/utils';
import { t } from '../../services/translation/translation';
import { getFullName, noop } from '../../utils/common';

type Props = {
  onClose: () => void;
  vehicleId: string;
  filterDriverKeys: string[];
};

export const AddDriverToVehicleModal = ({ onClose, vehicleId, filterDriverKeys }: Props) => {
  const [driverKeys, setDriverKeys] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const {
    data: drivers,
    isLoading: isLoadingDrivers,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteFetchDrivers({
    direction: 'asc',
    orderBy: 'firstName',
    limit: 100,
    search: debouncedSearchQuery,
  });

  const { mutate: assignDriver, isLoading } = useAssignDriverToVehicle();
  const filteredDrivers = useMemo(
    () =>
      drivers?.pages
        .map(p => p.items)
        .flat()
        .filter(driver => !filterDriverKeys.includes(driver.id))
        .filter(driver => driver.status === 'ACTIVATED') ?? [],
    [drivers, filterDriverKeys],
  );

  const handleClose = isLoading ? noop : onClose;

  const handleAssign = () => {
    if (driverKeys.length === 0) {
      ToastManager.show({ severity: 'warning', message: t('vehicle-route.warn-select') });
      return;
    }

    assignDriver({ vehicleId, driverKeys }, { onSuccess: () => onClose() });
  };

  return (
    <Modal onClose={handleClose}>
      <Modal.Header onClose={handleClose} title={t('vehicle-route.add-modal.title')} />
      <Modal.Body>
        <p className="text-sm font-normal text-gray-500 dark:text-gray-400 mb-4">
          {t('vehicle-route.add-modal.description')}
        </p>
        <TextInput
          placeholder={t('invoicing-route.search-driver')}
          className="min-w-full xl:w-56 xl:min-w-0 mb-4"
          value={searchQuery}
          onChange={e => setSearchQuery(e.currentTarget.value)}
          icon={<MagnifyingGlassSolid width={16} height={16} className="fill-current" />}
        />
        {filteredDrivers.length > 0 ? (
          <>
            <button
              className="text-sm font-medium text-blue-600 dark:text-blue-500 hover:underline"
              onClick={() => setDriverKeys(filteredDrivers.map(d => d.id))}
            >
              Select all
            </button>{' '}
            /{' '}
            <button
              className="text-sm font-medium text-blue-600 dark:text-blue-500 hover:underline"
              onClick={() => setDriverKeys([])}
            >
              Unselect all
            </button>
          </>
        ) : null}
        <div className="w-full bg-white divide-y divide-gray-100 rounded-lg shadow dark:bg-gray-800 dark:divide-gray-600 max-h-40 overflow-y-auto">
          <ul
            className="p-3 space-y-1 text-sm text-gray-700 dark:text-gray-200"
            aria-labelledby="dropdownRadioBgHoverButton"
          >
            {isLoadingDrivers ? <Spinner /> : null}
            {filteredDrivers.map(driver => (
              <li key={driver.id}>
                <div className="flex items-center pl-2 rounded hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer">
                  <input
                    id={`assign-radio-${driver.id}`}
                    type="checkbox"
                    checked={driverKeys.includes(driver.id)}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-700 dark:focus:ring-offset-gray-700 focus:ring-2 dark:bg-gray-600 dark:border-gray-500 cursor-pointer"
                    onChange={e => {
                      if (e.currentTarget.checked) {
                        setDriverKeys(keys => [...keys, driver.id]);
                      } else {
                        setDriverKeys(keys => keys.filter(d => d !== driver.id));
                      }
                    }}
                  />
                  <label
                    htmlFor={`assign-radio-${driver.id}`}
                    className="w-full ml-2 p-2 text-sm font-medium text-gray-900 rounded dark:text-gray-300 cursor-pointer"
                  >
                    {getFullName(driver.firstName, driver.lastName)}
                  </label>
                </div>
              </li>
            ))}
            {!isLoadingDrivers && filteredDrivers.length === 0 && searchQuery.length > 0 ? (
              <p className="text-gray-500 dark:text-gray-400 italic">{t('vehicle-route.add-modal.no-driver-found')}</p>
            ) : !isLoadingDrivers && filteredDrivers.length === 0 && searchQuery.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 italic">{t('vehicle-route.add-modal.no-driver')}</p>
            ) : null}
          </ul>
        </div>
        <div>
          {hasNextPage ? (
            <div className="flex justify-center mt-3">
              <PrimaryButton
                onClick={() => {
                  fetchNextPage();
                }}
                isLoading={isFetchingNextPage}
              >
                {t('common.see-more')}
              </PrimaryButton>
            </div>
          ) : null}
          <div>{isLoading ? <Spinner /> : null}</div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <GradientButton onClick={handleAssign} isLoading={isLoading}>
          {t('common.add')}
        </GradientButton>
        <SecondaryButton onClick={handleClose}>{t('common.cancel')}</SecondaryButton>
      </Modal.Footer>
    </Modal>
  );
};
